import { useEffect, useRef } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import "@/App.css";
import { motion, useInView } from "framer-motion";
import { CoreValuesPage, Navigation, Footer } from "@/pages/CoreValues";

// Logo URLs - Cleaned SVGs with tight viewBox (no whitespace)
const LOGO_HEADER_SVG = "/rise-header.svg";
const LOGO_FOOTER_SVG = "/rise-footer.svg";
const LOGO_ROUND = "https://customer-assets.emergentagent.com/job_rise-goldcoast/artifacts/i8yqrulb_8.png";
const LOGO_HERO = "/rise-hero-logo.svg";

// Section Images from presentation
const IMAGE_HERO = "https://customer-assets.emergentagent.com/job_rise-goldcoast/artifacts/prr3dcdt_image4.png"; // Gold Coast aerial
const IMAGE_PODCAST = "https://customer-assets.emergentagent.com/job_rise-goldcoast/artifacts/akx4ek5d_image6.png"; // Podcast/conversation
const IMAGE_COMMUNITY = "https://customer-assets.emergentagent.com/job_rise-goldcoast/artifacts/ucntvm0o_image2.png"; // Community walking
const IMAGE_MAP = "https://customer-assets.emergentagent.com/job_rise-goldcoast/artifacts/prr3dcdt_image4.png"; // Gold Coast map/aerial

// Focus Area Images (PNG files)
const IMAGE_MENTAL_HEALTH = "https://customer-assets.emergentagent.com/job_f1c13ddf-0d5f-4b26-b02a-605b00994641/artifacts/76jtsq0h_Mental%20Health%20image%20.png";
const IMAGE_DOMESTIC_VIOLENCE = "https://customer-assets.emergentagent.com/job_f1c13ddf-0d5f-4b26-b02a-605b00994641/artifacts/fjfwteap_Domestic%20Violence%20Image%20.png";
const IMAGE_SOCIAL_VULNERABILITY = "https://customer-assets.emergentagent.com/job_f1c13ddf-0d5f-4b26-b02a-605b00994641/artifacts/zcn78xl0_Social%20Vulnerability%20Image.png";
const IMAGE_HOMELESSNESS = "https://customer-assets.emergentagent.com/job_f1c13ddf-0d5f-4b26-b02a-605b00994641/artifacts/no1h3tma_Homelessness%20Image%20.png";

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] }
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15 }
  }
};

// Reusable animated section wrapper
const AnimatedSection = ({ children, className = "", id = "", "data-testid": testId }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  
  return (
    <motion.section
      ref={ref}
      id={id}
      className={className}
      data-testid={testId}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={fadeInUp}
    >
      {children}
    </motion.section>
  );
};

// ============================================
// SECTION 1: HERO
// ============================================
const HeroSection = () => {
  return (
    <section data-testid="hero-section" className="min-h-screen relative overflow-hidden flex items-center">
      
      {/* Background Image Layer */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `url(${IMAGE_HERO})`,
          backgroundPosition: 'right center',
        }}
        data-testid="hero-bg-image"
      />
      
      {/* Gradient Overlay */}
      <div 
        className="absolute inset-0"
        style={{
          background: 'linear-gradient(to right, rgba(90,153,212,0.95) 0%, rgba(90,153,212,0.95) 40%, rgba(90,153,212,0.55) 100%)',
        }}
        data-testid="hero-gradient"
      />
      
      {/* Hero Content */}
      <div className="relative z-10 w-full py-20 lg:py-0">
        <div className="rise-container">
          
          {/* Stacked Content - Left aligned */}
          <div className="max-w-[850px]">
            
            {/* Logo - Fades in first */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1], delay: 0 }}
              className="mb-4 lg:mb-5"
            >
              <img 
                src={LOGO_HERO} 
                alt="Project RISE" 
                className="h-[3.5rem] md:h-[5rem] lg:h-[8rem] w-auto object-contain block"
                data-testid="hero-logo"
              />
            </motion.div>

            {/* Headline - Fades in second */}
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1], delay: 0.1 }}
              className="hero-headline-cinematic text-black mb-6 lg:mb-8" 
              data-testid="hero-headline"
            >
              A collaborative model<span className="text-[#38b6ff]">.</span><br />
              Empowering individuals<span className="text-[#38b6ff]">.</span><br />
              Building autonomy for lasting change<span className="text-[#38b6ff]">.</span>
            </motion.h1>
            
            {/* Supporting text - Fades in third */}
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
              className="body-large text-black/80 max-w-xl mb-8 lg:mb-12" 
              data-testid="hero-subline"
            >
              Support individuals and families to move beyond domestic and family violence, mental health, social vulnerability, and homelessness through trauma informed practices and collaborative solutions.
            </motion.p>
            
            {/* Buttons - Fades in last */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1], delay: 0.3 }}
              className="flex flex-col sm:flex-row gap-4"
            >
              <a 
                href="/core-values" 
                className="btn-primary-rounded"
                data-testid="core-values-btn"
              >
                Our Core Values
              </a>
              <a 
                href="#focus-areas" 
                className="btn-secondary-rounded"
                data-testid="focus-areas-btn"
              >
                Our Focus Areas
              </a>
            </motion.div>
            
          </div>
          
        </div>
      </div>
    </section>
  );
};

// ============================================
// SECTION 2: THE MISSION
// ============================================
const MissionSection = () => {
  return (
    <AnimatedSection id="mission" className="py-20 lg:py-32 bg-white" data-testid="mission-section">
      <div className="rise-container">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          
          {/* Left Column: Text Content */}
          <motion.div variants={fadeInUp}>
            {/* Logo */}
            <img 
              src={LOGO_HEADER_SVG} 
              alt="Project RISE" 
              className="w-[180px] h-[180px] lg:w-[220px] lg:h-[220px] object-contain mb-6"
              data-testid="mission-logo"
            />
            
            <h2 className="heading-tagline text-[#737373] mb-8" data-testid="mission-tagline">
              Together. On Purpose.
            </h2>
            
            <div className="space-y-6">
              <p className="body-large text-black" data-testid="mission-text-1">
                Rise is a collaborative model of care that empowers individuals and builds autonomy for lasting change.
              </p>
              
              <p className="body-large text-black" data-testid="mission-text-2">
                We provide immediate critical support while developing sustainable, long-term solutions. Breaking generational cycles of mental health challenges, domestic violence, vulnerable communities & homelessness.
              </p>
            </div>
          </motion.div>
          
          {/* Right Column: Image Grid */}
          <motion.div 
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-2 gap-4"
          >
            {/* Top row - two images */}
            <motion.div variants={fadeInUp} className="aspect-square overflow-hidden rounded-xl">
              <img 
                src={IMAGE_PODCAST} 
                alt="Podcast studio conversation" 
                className="w-full h-full object-cover"
                data-testid="mission-image-podcast"
              />
            </motion.div>
            <motion.div variants={fadeInUp} className="aspect-square overflow-hidden rounded-xl">
              <img 
                src={IMAGE_MAP} 
                alt="Gold Coast aerial map" 
                className="w-full h-full object-cover"
                data-testid="mission-image-map"
              />
            </motion.div>
            
            {/* Bottom row - full width image */}
            <motion.div variants={fadeInUp} className="col-span-2 aspect-[2/1] overflow-hidden rounded-xl">
              <img 
                src={IMAGE_COMMUNITY} 
                alt="Community walking together" 
                className="w-full h-full object-cover"
                data-testid="mission-image-community"
              />
            </motion.div>
          </motion.div>
          
        </div>
      </div>
    </AnimatedSection>
  );
};

// ============================================
// SECTION 3: KEY FOCUS AREAS
// ============================================
const FocusAreasSection = () => {
  return (
    <AnimatedSection id="focus-areas" data-testid="focus-areas-section">
      {/* Light Blue Header Section */}
      <div className="bg-[#5A99D4]/30 py-16 lg:py-24">
        <div className="rise-container text-center">
          {/* Logo */}
          <img 
            src={LOGO_HEADER_SVG} 
            alt="Project RISE" 
            className="w-[140px] h-[140px] lg:w-[180px] lg:h-[180px] object-contain mx-auto mb-4"
            data-testid="focus-logo"
          />
          
          <h2 className="heading-section text-black mb-2" data-testid="focus-headline">
            Reach. Innovate. Support. Empower.
          </h2>
        </div>
      </div>
      
      {/* White Content Section with Focus Areas */}
      <div className="bg-white py-16 lg:py-20">
        <div className="rise-container">
          {/* Description */}
          <div className="max-w-3xl mx-auto text-center mb-16">
            <p className="body-large text-[#737373] mb-4" data-testid="focus-subtext">
              Mental health, domestic violence, homelessness, and social vulnerability are interconnected challenges. Real change requires addressing them together.
            </p>
            <p className="body-large text-[#737373]" data-testid="focus-subtext-2">
              RISE works across all four areas simultaneously to break generational cycles and build lasting independence.
            </p>
          </div>
          
          {/* Four Image Blocks */}
          <motion.div 
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12"
          >
            {/* Mental Health */}
            <motion.div 
              variants={fadeInUp}
              className="flex flex-col items-center text-center group cursor-pointer"
              data-testid="focus-mental-health"
            >
              <div className="w-20 h-20 lg:w-24 lg:h-24 flex items-center justify-center mb-6 transition-transform duration-300 group-hover:scale-105">
                <img 
                  src={IMAGE_MENTAL_HEALTH} 
                  alt="Mental Health" 
                  className="w-full h-full object-contain"
                />
              </div>
              <h3 className="font-montserrat font-bold text-black text-base lg:text-lg">Mental Health</h3>
            </motion.div>
            
            {/* Domestic Violence */}
            <motion.div 
              variants={fadeInUp}
              className="flex flex-col items-center text-center group cursor-pointer"
              data-testid="focus-domestic-violence"
            >
              <div className="w-20 h-20 lg:w-24 lg:h-24 flex items-center justify-center mb-6 transition-transform duration-300 group-hover:scale-105">
                <img 
                  src={IMAGE_DOMESTIC_VIOLENCE} 
                  alt="Domestic Violence" 
                  className="w-full h-full object-contain"
                />
              </div>
              <h3 className="font-montserrat font-bold text-black text-base lg:text-lg">Domestic Violence</h3>
            </motion.div>
            
            {/* Social Vulnerability */}
            <motion.div 
              variants={fadeInUp}
              className="flex flex-col items-center text-center group cursor-pointer"
              data-testid="focus-social-vulnerability"
            >
              <div className="w-20 h-20 lg:w-24 lg:h-24 flex items-center justify-center mb-6 transition-transform duration-300 group-hover:scale-105">
                <img 
                  src={IMAGE_SOCIAL_VULNERABILITY} 
                  alt="Social Vulnerability" 
                  className="w-full h-full object-contain"
                />
              </div>
              <h3 className="font-montserrat font-bold text-black text-base lg:text-lg">Social Vulnerability</h3>
            </motion.div>
            
            {/* Homelessness */}
            <motion.div 
              variants={fadeInUp}
              className="flex flex-col items-center text-center group cursor-pointer"
              data-testid="focus-homelessness"
            >
              <div className="w-20 h-20 lg:w-24 lg:h-24 flex items-center justify-center mb-6 transition-transform duration-300 group-hover:scale-105">
                <img 
                  src={IMAGE_HOMELESSNESS} 
                  alt="Homelessness" 
                  className="w-full h-full object-contain"
                />
              </div>
              <h3 className="font-montserrat font-bold text-black text-base lg:text-lg">Homelessness</h3>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </AnimatedSection>
  );
};

// ============================================
// SECTION 4: THE RISE MODEL
// ============================================
const RiseModelSection = () => {
  return (
    <AnimatedSection id="rise-model" className="py-20 lg:py-32 bg-white" data-testid="rise-model-section">
      <div className="rise-container">
        
        {/* Section Header */}
        <div className="text-center mb-12 lg:mb-16">
          {/* Logo */}
          <img 
            src={LOGO_HEADER_SVG} 
            alt="Project RISE" 
            className="w-[120px] h-[120px] lg:w-[160px] lg:h-[160px] object-contain mx-auto mb-6"
            data-testid="model-logo"
          />
          
          <p className="font-poppins font-medium text-lg lg:text-xl text-[#737373] tracking-wide" data-testid="model-tagline">
            Reach . Innovate . Support. Empower
          </p>
        </div>
        
        {/* Four Pillar Cards */}
        <motion.div 
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
        >
          {/* Reach Card */}
          <motion.div 
            variants={fadeInUp}
            className="bg-[#f5f5f5] rounded-2xl p-6 lg:p-8"
            data-testid="pillar-reach"
          >
            {/* Icon */}
            <div className="flex justify-center mb-6">
              <ReachIcon />
            </div>
            
            {/* Title */}
            <h3 className="font-montserrat font-bold text-xl text-black italic mb-6 text-center">
              Reach
            </h3>
            
            {/* Content */}
            <ul className="space-y-3">
              <li className="font-poppins text-black">Government Alignment</li>
              <li className="font-poppins text-black">Crisis Outreach Points</li>
              <li className="font-poppins text-black">Intake Partners</li>
              <li className="font-poppins text-black">Strategic Alliances</li>
              <li className="font-poppins text-black">Marketing & Media</li>
              <li className="font-poppins text-black">Podcasts & Education</li>
              <li className="font-poppins text-black">Events & Conferences</li>
            </ul>
          </motion.div>
          
          {/* Innovate Card */}
          <motion.div 
            variants={fadeInUp}
            className="bg-[#f5f5f5] rounded-2xl p-6 lg:p-8"
            data-testid="pillar-innovate"
          >
            {/* Icon */}
            <div className="flex justify-center mb-6">
              <InnovateIcon />
            </div>
            
            {/* Title */}
            <h3 className="font-montserrat font-bold text-xl text-black italic mb-6 text-center">
              Innovate
            </h3>
            
            {/* Content */}
            <div className="space-y-4">
              <p className="font-poppins text-black">MAP Pathway</p>
              
              <div className="space-y-1">
                <p className="font-poppins font-semibold text-black underline">Meet</p>
                <p className="font-poppins text-[#737373] text-sm">Meet them right where they are</p>
              </div>
              
              <div className="space-y-1">
                <p className="font-poppins font-semibold text-black underline">Assess</p>
                <p className="font-poppins text-[#737373] text-sm">Assessment & Discovery Sessions</p>
              </div>
              
              <div className="space-y-1">
                <p className="font-poppins font-semibold text-black underline">Pathway</p>
                <p className="font-poppins text-[#737373] text-sm">Individualised Pathway Plan</p>
              </div>
            </div>
          </motion.div>
          
          {/* Support Card */}
          <motion.div 
            variants={fadeInUp}
            className="bg-[#f5f5f5] rounded-2xl p-6 lg:p-8"
            data-testid="pillar-support"
          >
            {/* Icon */}
            <div className="flex justify-center mb-6">
              <SupportIcon />
            </div>
            
            {/* Title */}
            <h3 className="font-montserrat font-bold text-xl text-black italic mb-6 text-center">
              Support
            </h3>
            
            {/* Content */}
            <ul className="space-y-3">
              <li className="font-poppins text-black">Safety First</li>
              <li className="font-poppins text-black">Housing</li>
              <li className="font-poppins text-black">Food & Clothing</li>
              <li className="font-poppins text-black">Mental & Emotional</li>
              <li className="font-poppins text-black">Social & Environmental</li>
              <li className="font-poppins text-black">Community Connection</li>
            </ul>
          </motion.div>
          
          {/* Empower Card */}
          <motion.div 
            variants={fadeInUp}
            className="bg-[#f5f5f5] rounded-2xl p-6 lg:p-8"
            data-testid="pillar-empower"
          >
            {/* Icon */}
            <div className="flex justify-center mb-6">
              <EmpowerIcon />
            </div>
            
            {/* Title */}
            <h3 className="font-montserrat font-bold text-xl text-black italic mb-6 text-center">
              Empower
            </h3>
            
            {/* Content */}
            <ul className="space-y-3">
              <li className="font-poppins text-black">Education & Upskilling</li>
              <li className="font-poppins text-black">Work & Employement</li>
              <li className="font-poppins text-black">Volunteer & Internships</li>
              <li className="font-poppins text-black">Personal Development</li>
              <li className="font-poppins text-black">Life & Competency</li>
              <li className="font-poppins text-black">Sustainability</li>
            </ul>
          </motion.div>
        </motion.div>
        
      </div>
    </AnimatedSection>
  );
};

// Icons for RISE Model pillars (matching the design)
const ReachIcon = () => (
  <svg viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="w-12 h-12 text-black">
    {/* Alarm/Siren icon */}
    <path d="M24 4V8" />
    <path d="M24 8C18 8 14 14 14 20V32H34V20C34 14 30 8 24 8Z" />
    <path d="M10 32H38" />
    <path d="M14 32V36H34V32" />
    <path d="M18 36V40H30V36" />
    <path d="M8 14L12 18" />
    <path d="M40 14L36 18" />
  </svg>
);

const InnovateIcon = () => (
  <svg viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="w-12 h-12 text-black">
    {/* Lightbulb with gear icon */}
    <path d="M24 4C18 4 14 10 14 16C14 20 16 24 18 26V32H30V26C32 24 34 20 34 16C34 10 30 4 24 4Z" />
    <path d="M18 32H30V36H18V32Z" />
    <path d="M20 36H28V40H20V36Z" />
    {/* Gear inside */}
    <circle cx="24" cy="16" r="4" />
    <path d="M24 10V12" />
    <path d="M24 20V22" />
    <path d="M18 16H20" />
    <path d="M28 16H30" />
    {/* Radiating lines */}
    <path d="M8 16H10" />
    <path d="M38 16H40" />
    <path d="M12 8L14 10" />
    <path d="M34 10L36 8" />
  </svg>
);

const SupportIcon = () => (
  <svg viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="w-12 h-12 text-black">
    {/* Network/Community icon */}
    <circle cx="24" cy="20" r="6" />
    <circle cx="12" cy="12" r="4" />
    <circle cx="36" cy="12" r="4" />
    <circle cx="8" cy="32" r="4" />
    <circle cx="40" cy="32" r="4" />
    <circle cx="24" cy="42" r="4" />
    {/* Connection lines */}
    <path d="M15 14L20 18" />
    <path d="M33 14L28 18" />
    <path d="M11 28L20 24" />
    <path d="M37 28L28 24" />
    <path d="M24 38L24 26" />
  </svg>
);

const EmpowerIcon = () => (
  <svg viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="w-12 h-12 text-black">
    {/* Briefcase icon */}
    <rect x="6" y="16" width="36" height="26" rx="2" />
    <path d="M16 16V12C16 10 18 8 20 8H28C30 8 32 10 32 12V16" />
    <path d="M6 24H42" />
    <path d="M20 24V28H28V24" />
  </svg>
);

// ============================================
// MAIN APP COMPONENT
// ============================================

// Home Page Component
const HomePage = () => {
  useEffect(() => {
    document.title = "Project RISE | Gold Coast";
  }, []);

  return (
    <div className="HomePage" data-testid="home-page">
      <Navigation />
      <HeroSection />
      <MissionSection />
      <FocusAreasSection />
      <RiseModelSection />
      <Footer />
    </div>
  );
};

function App() {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/core-values" element={<CoreValuesPage />} />
      </Routes>
    </Router>
  );
}

export default App;
